import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SubscriptionService from '../services/subscription.service';
import { UserContext } from '../context/UserContext';
import axios from 'axios';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.adsmash.org/api'; // Live server

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const { updateUser } = useContext(UserContext);

  useEffect(() => {
    const verifyAndRedirect = async () => {
      const sessionId = searchParams.get('session_id');
      const paymentMethod = searchParams.get('payment_method');
      const tokenParam = searchParams.get('token'); // PayPal token
      const payerId = searchParams.get('PayerID'); // PayPal PayerID
      const orderId = searchParams.get('orderId'); // PayPal order ID (if we stored it)
      const email = sessionStorage.getItem('pending_signup_email');
      const storedOrderId = sessionStorage.getItem('paypal_order_id');
      const token = localStorage.getItem('token');
      const isLoggedIn = !!token;
      
      // Handle PayPal return
      // Only process if we have payment_method=paypal AND a token/PayerID (indicates user approved)
      // If user cancelled, they should be redirected to payment-cancelled page, not here
      if (paymentMethod === 'paypal' && (tokenParam || payerId)) {
        try {
          setIsVerifying(true);
          setError(null);
          
          // Get order ID from URL or session storage
          const finalOrderId = orderId || storedOrderId;
          if (!finalOrderId) {
            throw new Error('PayPal order ID not found');
          }
          
          // Check if this is a cancellation (user might have been redirected here by mistake)
          const cancelled = searchParams.get('canceled') === 'true' || searchParams.get('cancelled') === 'true';
          if (cancelled) {
            // User cancelled, redirect to cancelled page
            navigate('/payment-cancelled', { replace: true });
            return;
          }
          
          // Capture PayPal payment (backend will verify order is APPROVED before capturing)
          const captureResponse = await axios.post(
            `${API_URL}/subscription/capture-paypal-order`,
            {
              orderId: finalOrderId,
              email: email || '',
              name: sessionStorage.getItem('pending_signup_name') || 'User'
            },
            {
              headers: token ? {
                Authorization: `Bearer ${token}`
              } : {},
              timeout: 30000
            }
          );
          
          if (!captureResponse.data.success) {
            // If capture failed (e.g., order not approved), redirect to cancelled page
            const errorMsg = captureResponse.data.error || 'Failed to capture PayPal payment';
            if (errorMsg.includes('not approved') || errorMsg.includes('not completed')) {
              navigate('/payment-cancelled', { replace: true });
              return;
            }
            throw new Error(errorMsg);
          }
          
          // If logged in, update user context
          if (isLoggedIn && captureResponse.data.user && updateUser) {
            updateUser(captureResponse.data.user);
            
            // Update localStorage
            if (captureResponse.data.subscription) {
              localStorage.setItem('subscription_status', captureResponse.data.subscription.status || 'active');
              localStorage.setItem('subscription_plan', captureResponse.data.subscription.plan || 'PREMIUM');
            }
          } else if (!isLoggedIn && captureResponse.data.isGuest) {
            // Guest user - store PayPal info for account creation
            sessionStorage.setItem('paypal_order_id', finalOrderId);
            sessionStorage.setItem('paypal_capture_id', captureResponse.data.paypalCaptureId);
            sessionStorage.setItem('pending_signup_email', captureResponse.data.email);
          }
          
          // Clear PayPal order ID from session
          sessionStorage.removeItem('paypal_order_id');
          
          setIsSuccess(true);
          setIsVerifying(false);
          
          // Redirect based on login status
          setTimeout(() => {
            if (isLoggedIn) {
              navigate('/dashboard', { replace: true });
            } else {
              navigate('/create-password', { replace: true });
            }
          }, 2000);
          
          return;
        } catch (paypalError: any) {
          console.error('PayPal capture error:', paypalError);
          setError(paypalError.response?.data?.error || paypalError.message || 'Failed to process PayPal payment');
          setIsVerifying(false);
          return;
        }
      }
      
      // STRIPE: If we have a session_id, user already registered and paid via Stripe
      if (sessionId) {
        try {
          setIsVerifying(true);
          // Verify the Stripe session (if Stripe is enabled later)
          const verifyResponse = await SubscriptionService.verifySession(sessionId);
          
          // Update UserContext with the latest user data
          if (verifyResponse.user && updateUser) {
            updateUser(verifyResponse.user);
          } else if (token) {
            // If updateUser not available, fetch user data directly
            try {
              const userResponse = await axios.get(`${API_URL}/user/me`, {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              });
              if (userResponse.data.success && userResponse.data.user && updateUser) {
                updateUser(userResponse.data.user);
              }
            } catch (err) {
              console.error('Error fetching user data:', err);
            }
          }
          
          // Clear sessionStorage
          sessionStorage.removeItem('pending_signup_email');
          sessionStorage.removeItem('pending_signup_name');
          
          // Redirect to dashboard (user is now premium)
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } catch (error) {
          console.error('Error verifying session:', error);
          // On error, still try to redirect to dashboard
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
        return;
      }
      
      // For logged-in users who just paid - refresh user data to get updated subscription
      if (isLoggedIn && token) {
        try {
          setIsVerifying(true);
          setError(null);
          
          console.log('Payment successful - Refreshing user data to verify premium status...');
          
          // Fetch fresh user data to get updated subscription status from backend
          const userResponse = await axios.get(`${API_URL}/user/me`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          console.log('Updated user data after payment:', userResponse.data);
          
          if (userResponse.data.success && userResponse.data.user) {
            const subscription = userResponse.data.user.subscription;
            console.log('User subscription status after payment:', subscription);
            
            // Update localStorage with fresh data
            if (subscription) {
              localStorage.setItem('subscription_status', subscription.status || 'active');
              localStorage.setItem('subscription_plan', subscription.plan || 'PREMIUM');
            }
            
            // Verify subscription is active and premium
            if (subscription && (subscription.status === 'active' || subscription.status === 'trialing') && 
                (subscription.plan === 'PREMIUM' || subscription.plan === 'BASIC' || subscription.plan === 'PRO')) {
              // Update UserContext with fresh premium user data
              if (updateUser) {
                updateUser(userResponse.data.user);
              }
              console.log('Premium membership confirmed - User context updated successfully');
              
              // Mark as success
              setIsSuccess(true);
              setIsVerifying(false);
              
              // Clear sessionStorage
              sessionStorage.removeItem('pending_payment_email');
              sessionStorage.removeItem('pending_signup_email');
              sessionStorage.removeItem('pending_signup_name');
              
              // Redirect to dashboard after showing success message
              setTimeout(() => {
                navigate('/dashboard', { replace: true });
              }, 2000);
            } else {
              // Subscription not properly updated - but payment was successful
              console.warn('Warning: Payment successful but subscription status not updated correctly:', subscription);
              
              // Still mark as success since payment went through
              // The backend should have updated it, but we'll force it
              localStorage.setItem('subscription_status', 'active');
              localStorage.setItem('subscription_plan', 'PREMIUM');
              
              if (updateUser) {
                // Update user with premium status
                const updatedUser = {
                  ...userResponse.data.user,
                  subscription: {
                    ...userResponse.data.user.subscription,
                    status: 'active',
                    plan: 'PREMIUM'
                  }
                };
                updateUser(updatedUser);
              }
              
              setIsSuccess(true);
              setIsVerifying(false);
              
              setTimeout(() => {
                navigate('/dashboard', { replace: true });
              }, 2000);
            }
          } else {
            setIsVerifying(false);
            setError('Payment successful but failed to verify subscription. Please refresh the page.');
          }
        } catch (error: any) {
          console.error('Error refreshing user data after payment:', error);
          console.error('Error response:', error.response?.data);
          
          // Even if refresh fails, payment was successful
          // Set premium status in localStorage
          localStorage.setItem('subscription_status', 'active');
          localStorage.setItem('subscription_plan', 'PREMIUM');
          
          setIsVerifying(false);
          setIsSuccess(true);
          
          // Redirect to dashboard
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 2000);
        }
        return;
      }
      
      // If user is NOT logged in, redirect to create password page
      if (!email) {
        // If no email found, redirect to home
        navigate('/');
        return;
      }

      // Redirect to create password page after a brief delay
      const timer = setTimeout(() => {
        navigate('/create-password');
      }, 2000);

      return () => clearTimeout(timer);
    };

    verifyAndRedirect();
  }, [navigate, searchParams, updateUser]);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
    >
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-lg w-full bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-10 text-center shadow-2xl">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8">
            <path
              fillRule="evenodd"
              d="M10.378 3.21c.755-2.274 3.189-2.274 3.944 0l.745 2.245c.118.355.45.599.826.599h2.362c2.386 0 3.366 1.927 2.19 4.162l-1.318 2.474a1.104 1.104 0 0 0 0 1.013l1.318 2.474c1.176 2.235.196 4.162-2.19 4.162h-2.362c-.376 0-.708.244-.826.6l-.745 2.244c-.755 2.274-3.189 2.274-3.944 0l-.745-2.245a.907.907 0 0 0-.826-.599H6.445c-2.386 0-3.366-1.927-2.19-4.162l1.318-2.474a1.104 1.104 0 0 0 0-1.013L4.255 10.216c-1.176-2.235-.196-4.162 2.19-4.162h2.362c.376 0 .708-.244.826-.6l.745-2.244Zm4.522 8.79a.9.9 0 0 0-1.246-1.3l-2.955 2.83-1.352-1.295a.9.9 0 0 0-1.246 1.3l1.975 1.891a.9.9 0 0 0 1.246 0l3.578-3.426Z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        {error ? (
          <>
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10 text-red-400">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8">
                <path
                  fillRule="evenodd"
                  d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-white mb-3">Payment Activation Failed</h1>
            <p className="text-white/70 mb-6">{error}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setError(null);
                  setIsVerifying(true);
                  window.location.reload();
                }}
                className="btn-adsmash-primary px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition hover:opacity-90"
              >
                Retry Activation
              </button>
              <button
                onClick={() => navigate('/dashboard', { replace: true })}
                className="px-6 py-3 rounded-lg text-white font-semibold border border-white/20 hover:bg-white/10 transition"
              >
                Go to Dashboard
              </button>
            </div>
          </>
        ) : isSuccess ? (
          <>
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8">
                <path
                  fillRule="evenodd"
                  d="M10.378 3.21c.755-2.274 3.189-2.274 3.944 0l.745 2.245c.118.355.45.599.826.599h2.362c2.386 0 3.366 1.927 2.19 4.162l-1.318 2.474a1.104 1.104 0 0 0 0 1.013l1.318 2.474c1.176 2.235.196 4.162-2.19 4.162h-2.362c-.376 0-.708.244-.826.6l-.745 2.244c-.755 2.274-3.189 2.274-3.944 0l-.745-2.245a.907.907 0 0 0-.826-.599H6.445c-2.386 0-3.366-1.927-2.19-4.162l1.318-2.474a1.104 1.104 0 0 0 0-1.013L4.255 10.216c-1.176-2.235-.196-4.162 2.19-4.162h2.362c.376 0 .708-.244.826-.6l.745-2.244Zm4.522 8.79a.9.9 0 0 0-1.246-1.3l-2.955 2.83-1.352-1.295a.9.9 0 0 0-1.246 1.3l1.975 1.891a.9.9 0 0 0 1.246 0l3.578-3.426Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-white mb-3">Payment Complete 🎉</h1>
            <p className="text-white/70 mb-8">
              Thanks for subscribing to AdSmash Premium! Redirecting to your dashboard...
            </p>
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-400"></div>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-white mb-3">Payment Complete 🎉</h1>
            <p className="text-white/70 mb-8">
              {isVerifying 
                ? 'Activating your premium membership...' 
                : searchParams.get('session_id')
                  ? 'Thanks for subscribing to AdSmash Premium!'
                  : 'Thanks for subscribing to AdSmash Premium! Now let\'s create your account password.'}
            </p>

            {isVerifying && (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-400"></div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
    <Footer />
  </div>
  );
};

export default PaymentSuccess;

